import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mail, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';
import { NofeeNavLogo } from '../components/NoFeeLogo';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /customer/auth/magic?token=XYZ
 *
 * Reads the magic-link token from the URL, exchanges it via
 * POST /api/customers/auth/magic-link/verify, stores the customer JWT, and
 * navigates to /customer/rewards. All states (verifying, success, error)
 * render premium dark-mode UI consistent with /customer.
 */
const CustomerMagicAuth = () => {
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');
  // Guard against React 18 StrictMode double-invocation of effects in dev,
  // which would otherwise race two POSTs against a single-use token and
  // always end in 'error' state in preview builds.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const token = (params.get('token') || '').trim();
        if (!token) {
          setStatus('error');
          setErrorMsg('Missing sign-in token. Please request a new link.');
          return;
        }
        const res = await axios.post(`${API}/api/customers/auth/magic-link/verify`, { token });
        if (res.data?.token) {
          localStorage.setItem('nofee_customer_token', res.data.token);
          setStatus('success');
          setTimeout(() => {
            window.location.href = '/customer/rewards';
          }, 700);
        } else {
          setStatus('error');
          setErrorMsg('Could not verify your sign-in link.');
        }
      } catch (err) {
        setStatus('error');
        setErrorMsg(
          err?.response?.data?.detail ||
            'This sign-in link is invalid or has expired. Please request a new one.',
        );
      }
    })();
  }, []);

  return (
    <div
      className="min-h-screen bg-black text-white antialiased flex flex-col"
      data-testid="customer-magic-auth-page"
    >
      <header className="absolute top-0 inset-x-0 z-10 px-6 lg:px-10 h-20 flex items-center">
        <a href="/" className="flex items-center" data-testid="magic-auth-logo">
          <NofeeNavLogo height={44} />
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <div
          className="relative w-full max-w-md rounded-3xl border border-gold-500/25 bg-gradient-to-b from-zinc-950 to-black p-8 sm:p-9 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.95)]"
          data-testid="magic-auth-card"
        >
          {/* Subtle gold glow */}
          <div
            aria-hidden="true"
            className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl pointer-events-none"
          />

          {status === 'verifying' && (
            <div className="relative text-center" data-testid="magic-auth-verifying">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-gold-500/[0.08] border border-gold-500/30 flex items-center justify-center mb-5">
                <Mail className="w-7 h-7 text-gold-400 animate-pulse" strokeWidth={1.6} />
              </div>
              <h1 className="text-[22px] font-extrabold text-white tracking-tight">
                Signing you in…
              </h1>
              <p className="mt-2 text-[13.5px] text-white/60 leading-relaxed">
                Verifying your secure sign-in link. This takes just a moment.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="relative text-center" data-testid="magic-auth-success">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-500/[0.10] border border-emerald-500/30 flex items-center justify-center mb-5">
                <CheckCircle2 className="w-7 h-7 text-emerald-300" strokeWidth={1.5} />
              </div>
              <h1 className="text-[22px] font-extrabold text-white tracking-tight">
                Welcome back!
              </h1>
              <p className="mt-2 text-[13.5px] text-white/60 leading-relaxed">
                You're signed in. Taking you to your NoFee Rewards…
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="relative text-center" data-testid="magic-auth-error">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-red-500/[0.10] border border-red-500/30 flex items-center justify-center mb-5">
                <AlertTriangle className="w-7 h-7 text-red-300" strokeWidth={1.5} />
              </div>
              <h1 className="text-[22px] font-extrabold text-white tracking-tight">
                Sign-in link unavailable
              </h1>
              <p className="mt-2 text-[13.5px] text-white/60 leading-relaxed">{errorMsg}</p>
              <a
                href="/customer?mode=login"
                data-testid="magic-auth-back-to-login"
                className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[48px] rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[13.5px] transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]"
              >
                Back to sign in <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CustomerMagicAuth;
